import { AsyncPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatButton } from '@angular/material/button'
import { MatChip, MatChipSet } from '@angular/material/chips'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatError, MatFormField } from '@angular/material/form-field'
import {
  ElderDurationInputComponent,
  ElderPaneContentComponent,
  ElderPaneHeaderComponent,
  ElderPaneTitleComponent,
  ElderSinglePaneWrapperComponent,
  ElderValidationErrorDirective,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable } from 'rxjs'
import { ShopfloorLayoutContext } from '../shopfloor-layout-context'
import { ShopfloorLayoutSpecEdge } from '../shopfloor-layout-spec-edge'
import { ShopfloorLayoutSpecNode } from '../shopfloor-layout-spec-node'
import { ShopfloorLayoutSpec } from '../spec/shopfloor-layout-spec'
import { TransportCost } from '../transport-cost'

export interface IShopFloorEditorEdgeDialogOptions {
  readonly context: ShopfloorLayoutContext
  readonly spec: ShopfloorLayoutSpec
  readonly edge: ShopfloorLayoutSpecEdge
}

enum Action {
  DELETE,
  UPDATE,
}

@Component({
  selector: 'app-shopfloor-layout-editor-edge-dialog',
  templateUrl: './shopfloor-layout-editor-edge-dialog.component.html',
  styleUrls: ['./shopfloor-layout-editor-edge-dialog.component.scss'],
  imports: [
    ElderSinglePaneWrapperComponent,
    ElderPaneHeaderComponent,
    ElderPaneTitleComponent,
    ElderPaneContentComponent,
    FormsModule,
    MatChipSet,
    MatChip,
    MatFormField,
    ElderDurationInputComponent,
    MatError,
    ElderValidationErrorDirective,
    MatButton,
    AsyncPipe,
    ElderTranslatePipe,
  ],
})
export class ShopfloorLayoutEditorEdgeDialogComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  private readonly _context$: BehaviorSubject<ShopfloorLayoutContext> = new BehaviorSubject(null)
  private readonly _spec$: BehaviorSubject<ShopfloorLayoutSpec> = new BehaviorSubject(null)

  public readonly edge: ShopfloorLayoutSpecEdge
  public transportDuration: string
  public readonly source: ShopfloorLayoutSpecNode
  public readonly target: ShopfloorLayoutSpecNode
  private dialogRef = inject<MatDialogRef<ShopfloorLayoutEditorEdgeDialogComponent>>(MatDialogRef)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const data = inject<IShopFloorEditorEdgeDialogOptions>(MAT_DIALOG_DATA)
    this.context = data.context
    this.spec = data.spec
    this.edge = data.edge
    this.transportDuration = this.edge.transportCost.duration
    this.source = this.context.nodes.find((n) => n.id === this.edge.source)
    this.target = this.context.nodes.find((n) => n.id === this.edge.target)
  }

  /***************************************************************************
   *                                                                         *
   * Getter / Setters                                                        *
   *                                                                         *
   **************************************************************************/

  public get context$(): Observable<ShopfloorLayoutContext> {
    return this._context$.asObservable()
  }

  public get context(): ShopfloorLayoutContext {
    return this._context$.getValue()
  }

  @Input()
  public set context(context: ShopfloorLayoutContext) {
    if (context) {
      this._context$.next(context)
    }
  }

  public get spec$(): Observable<ShopfloorLayoutSpec> {
    return this._spec$.asObservable()
  }

  public get spec(): ShopfloorLayoutSpec {
    return this._spec$.getValue()
  }

  @Input()
  public set spec(spec: ShopfloorLayoutSpec) {
    if (spec) {
      this._spec$.next(spec)
    }
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public onApply(): void {
    this.dialogRef.close(
      this.updateSpec(this.spec, this.edge, this.transportDuration, Action.UPDATE)
    )
  }

  public onDelete(): void {
    this.dialogRef.close(
      this.updateSpec(this.spec, this.edge, this.transportDuration, Action.DELETE)
    )
  }

  public onClose(): void {
    this.dialogRef.close()
  }

  public onDurationChange($event: any): void {
    this.log.debug('Transport duration of edge changed:', $event)
    this.transportDuration = $event
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private updateSpec(
    spec: ShopfloorLayoutSpec,
    edge: ShopfloorLayoutSpecEdge,
    transportDuration: string,
    action: Action
  ): ShopfloorLayoutSpec {
    if (action === Action.DELETE) {
      return new ShopfloorLayoutSpec(
        spec.id,
        spec.facility,
        spec.edges.filter((e) => e.id !== edge.id)
      )
    } else if (action === Action.UPDATE) {
      const edgeToUpdate = spec.edges.find((e) => e.id === edge.id)
      edgeToUpdate.transportCost = new TransportCost(transportDuration)
      return new ShopfloorLayoutSpec(spec.id, spec.facility, spec.edges)
    } else {
      return spec
    }
  }
}
